import config from 'constants/config'
import { OFFER_BOOKINGS_THRESHOLD, ROOM_BOOKINGS_THRESHOLD } from 'constants/offer'
import { RATE_PRICE_ROOMS_REMAINING_THRESHOLD } from './roomsRemainingLabelConstants'

/**
 * Single source of truth for `RoomsRemainingLabel`'s `threshold="rate"`/`copyVariant="room-count"`
 * visibility gate (the variant used on Flash package-tile rows) -- a room count of 0 or above the
 * threshold hides the label entirely.
 */
export function shouldShowRoomsRemainingLabel(roomCount: number): boolean {
  return roomCount > 0 && roomCount <= config.RATE_OPTION_ONLY_N_ROOMS_LEFT_THRESHOLD
}

export type RoomsRemainingLabelThreshold = 'rate' | 'default'
export type RoomsRemainingLabelCopyVariant = 'room-count' | 'rate-price'

/**
 * Single source of truth for `RoomsRemainingLabel`'s full visibility gate, covering all 3 real
 * threshold/copyVariant combinations the component is actually used with (default/room-count on
 * Flash's calendar day cells, rate/room-count -- see `shouldShowRoomsRemainingLabel` above -- and
 * rate/rate-price on Property/SPP room tiles). Matches the component's own
 * `roomCount === 0 || roomCount > minCount || (shouldShowRatePrice && !ratePrice)` check exactly.
 */
export function shouldShowRoomsRemainingLabelForVariant(params: {
  roomCount: number
  hasRatePrice: boolean
  threshold?: RoomsRemainingLabelThreshold
  copyVariant?: RoomsRemainingLabelCopyVariant
}): boolean {
  const { roomCount, hasRatePrice, threshold = 'default', copyVariant = 'room-count' } = params
  const shouldShowRatePrice = copyVariant === 'rate-price'
  let minCount = config.ONLY_N_ROOMS_LEFT_THRESHOLD
  if (threshold === 'rate') {
    minCount = shouldShowRatePrice ? RATE_PRICE_ROOMS_REMAINING_THRESHOLD : config.RATE_OPTION_ONLY_N_ROOMS_LEFT_THRESHOLD
  }

  return !(roomCount === 0 || roomCount > minCount || (shouldShowRatePrice && !hasRatePrice))
}

export type BookingLabelThreshold = 'offer' | 'room'

const BOOKING_LABEL_THRESHOLD: Record<BookingLabelThreshold, number> = {
  offer: OFFER_BOOKINGS_THRESHOLD,
  room: ROOM_BOOKINGS_THRESHOLD,
}

/** Single source of truth for `BookingsCountLabel`'s visibility gate. */
export function shouldShowBookingsCountLabel(bookings: number, threshold: BookingLabelThreshold): boolean {
  return bookings >= BOOKING_LABEL_THRESHOLD[threshold]
}
